import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaSearch, FaSpinner, FaExclamationTriangle, FaLock } from 'react-icons/fa';
import { rootUrl } from '../../../config/config';
import { formatQty } from './partsUi';
import SerialNumbersField from './SerialNumbersField';

const SEARCH_THRESHOLD = 10;
const MAX_ROWS = 100;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken') || 'default-token'}` });

/**
 * List of the serial numbers currently in stock for a part. Fitted, sold and removed units are
 * left to Serial Lookup, since they grow without limit over time.
 * When serial tracking was switched on before every unit had a serial ("pending"), this is also
 * where the missing serials are added; the part is blocked from stock movements until then.
 */
export default function PartSerialsPanel({ partId, quantity, pending = false, onPendingChange }) {
    const [serials, setSerials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [reloadKey, setReloadKey] = useState(0);
    const [newSerials, setNewSerials] = useState([]);
    const [captureError, setCaptureError] = useState('');
    const [captureNotice, setCaptureNotice] = useState('');
    const [capturing, setCapturing] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const res = await axios.get(`${rootUrl}/api/parts/${encodeURIComponent(partId)}/serials`, {
                    params: { status: 'IN_STOCK' },
                    headers: authHeaders()
                });
                if (!cancelled) setSerials(res.data?.data || []);
            } catch (err) {
                console.error('Failed to load serials:', err);
                if (!cancelled) setError('Could not load serial numbers. Close and reopen the part to try again.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [partId, reloadKey]);

    const matches = useMemo(() => {
        const term = search.trim().toUpperCase();
        return term ? serials.filter((s) => s.serial_no.includes(term)) : serials;
    }, [serials, search]);
    const visible = matches.slice(0, MAX_ROWS);

    const onHand = Number(quantity || 0);
    const missing = Math.max(onHand - serials.length, 0);

    const saveMissing = async () => {
        if (newSerials.length === 0) {
            setCaptureError('Enter at least one serial number, one per line.');
            return;
        }
        if (newSerials.length > missing) {
            setCaptureError(`Only ${formatQty(missing)} unit(s) are missing a serial number (got ${newSerials.length}).`);
            return;
        }
        if (new Set(newSerials).size !== newSerials.length) {
            setCaptureError('Each serial number can only be entered once.');
            return;
        }
        setCapturing(true);
        setCaptureError('');
        setCaptureNotice('');
        try {
            const res = await axios.post(
                `${rootUrl}/api/parts/${encodeURIComponent(partId)}/serials/missing`,
                { serials: newSerials },
                { headers: authHeaders() }
            );
            setCaptureNotice(res.data?.message || 'Serial numbers saved.');
            setNewSerials([]);
            setReloadKey((key) => key + 1);
            onPendingChange?.(res.data?.data?.pending === true);
        } catch (err) {
            setCaptureError(err.response?.data?.message || 'Could not save the serial numbers. Check them and try again.');
        } finally {
            setCapturing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                <FaSpinner className="animate-spin" /> Loading serial numbers...
            </div>
        );
    }
    if (error) return <p className="text-sm text-red-500">{error}</p>;

    return (
        <div className="space-y-3">
            <p className="text-sm text-gray-700">
                <span className="font-semibold">{serials.length}</span> serial number(s) in stock
            </p>

            {captureNotice && (
                <p className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">{captureNotice}</p>
            )}

            {pending && missing > 0 && (
                <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-3">
                    <div className="flex items-start gap-2 text-sm text-red-700">
                        <FaLock className="mt-0.5 shrink-0" />
                        <span>
                            <span className="font-semibold">{formatQty(missing)} unit(s) in stock have no serial number.</span>{' '}
                            This part can&apos;t be used in purchases, adjustments, transfers or job cards until every unit on hand has one.
                            You can add them in batches.
                        </span>
                    </div>
                    <div className="rounded-lg bg-white p-3">
                        <SerialNumbersField
                            mode="enter"
                            allowFewer
                            quantity={missing}
                            value={newSerials}
                            onChange={(list) => {
                                setNewSerials(list);
                                setCaptureError('');
                            }}
                            label="Add missing serials"
                        />
                        {captureError && <p className="mt-1 text-xs text-red-500">{captureError}</p>}
                        <div className="mt-3 flex justify-end">
                            <button
                                type="button"
                                onClick={saveMissing}
                                disabled={capturing || newSerials.length === 0}
                                className="inline-flex items-center gap-2 rounded-sm bg-[#FF5733] px-4 py-2 text-sm font-semibold text-white hover:bg-[#e64a2b] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {capturing && <FaSpinner className="animate-spin" />}
                                {capturing ? 'Saving...' : newSerials.length ? `Save ${newSerials.length} serial number(s)` : 'Save serial numbers'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!pending && serials.length !== onHand && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                    <FaExclamationTriangle className="mt-0.5 shrink-0" />
                    <span>
                        {serials.length} serial number(s) are in stock but the part shows {formatQty(onHand)} on hand.
                        Check recent transactions for this part.
                    </span>
                </div>
            )}

            {serials.length > SEARCH_THRESHOLD && (
                <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                    <input
                        type="search"
                        aria-label="Search serial numbers"
                        value={search}
                        onChange={(e) => setSearch(e.target.value.toUpperCase())}
                        placeholder="Search serial numbers"
                        className="w-full h-10 border border-gray-300 rounded-lg pl-8 pr-3 text-sm font-mono uppercase focus:outline-none focus:border-[#FF5733] focus:ring-1 focus:ring-[#FF5733]"
                    />
                </div>
            )}

            {serials.length === 0 ? (
                <p className="text-sm text-gray-500">{onHand > 0 ? 'No serial numbers recorded yet for the units in stock.' : 'No units of this part are in stock.'}</p>
            ) : matches.length === 0 ? (
                <p className="text-sm text-gray-500">No in-stock serial number matches that search.</p>
            ) : (
                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                    {visible.map((s) => (
                        <li key={s.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                            <Link
                                to={`/parts/serials?serial_no=${encodeURIComponent(s.serial_no)}`}
                                className="font-mono font-semibold text-[#FF5733] hover:underline break-all"
                                title="View movement history"
                            >
                                {s.serial_no}
                            </Link>
                            <span className="text-xs text-gray-600 text-right">{s.location || '-'}</span>
                        </li>
                    ))}
                </ul>
            )}
            {matches.length > MAX_ROWS && (
                <p className="text-xs text-gray-500">
                    Showing the first {MAX_ROWS} of {matches.length}. Search to find a specific serial number.
                </p>
            )}
            <p className="text-xs text-gray-500">
                Only units in stock are listed. Click a serial number to see its full history, or use Serial Lookup for fitted and sold units.
            </p>
        </div>
    );
}
